import React from 'react';
import { X } from 'lucide-react';
import { HIGHLIGHTERS, Highlighter } from '../../lib/paper';
import { InboxFilter } from '../../lib/desk';
import { WritingScribble } from '../paper';

const Tab: React.FC<{
  tone: Highlighter;
  count: number;
  label: string;
  active?: boolean;
  onClick?: () => void;
  children?: React.ReactNode;
}> = ({ tone, count, label, active, onClick, children }) => {
  const className =
    'inline-flex items-center gap-1 rounded-full border-[1.5px] border-solid py-1.5 pl-3 pr-3 font-body';
  const style = { backgroundColor: tone.fill, color: tone.ink, borderColor: active ? tone.ink : 'transparent' };
  const body = (
    <>
      <span className="text-sm font-bold leading-[18px]">{count}</span>
      <span className="text-[12.5px] font-semibold leading-[18px]">{label}</span>
      {children}
      {active && <X size={12} />}
    </>
  );
  if (!onClick) {
    return (
      <span className={className} style={style}>
        {body}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={`${count} ${label}${active ? ', showing only these — click to show all' : ''}`}
      className={`${className} cursor-pointer transition-transform duration-150 active:scale-[0.93]`}
      style={style}
    >
      {body}
    </button>
  );
};

/**
 * The desk at a glance as highlighter tabs (mobile's StatTabs): today's pages
 * and pages still being read narrow the inbox when clicked; this week's haul is
 * a plain count. Zero-count tabs step aside.
 */
export const StatTabs: React.FC<{
  today: number;
  reading: number;
  thisWeek: number;
  filter: InboxFilter;
  onFilter: (f: InboxFilter) => void;
}> = ({ today, reading, thisWeek, filter, onFilter }) => {
  const toggle = (f: InboxFilter) => () => onFilter(filter === f ? 'all' : f);
  return (
    <div className="flex flex-wrap gap-2">
      {today > 0 && (
        <Tab tone={HIGHLIGHTERS.mint} count={today} label="today" active={filter === 'today'} onClick={toggle('today')} />
      )}
      {reading > 0 && (
        <Tab
          tone={HIGHLIGHTERS.lilac}
          count={reading}
          label="reading"
          active={filter === 'reading'}
          onClick={toggle('reading')}
        >
          <WritingScribble width={18} lines={1} color={HIGHLIGHTERS.lilac.ink} className="ml-0.5" />
        </Tab>
      )}
      {thisWeek > 0 && <Tab tone={HIGHLIGHTERS.yellow} count={thisWeek} label="this week" />}
    </div>
  );
};
